/*
  TemplateVersionHistoryDialog — read/restore snapshots of a template.

  A snapshot is captured on the target template every time its content
  is overwritten (Replace-with or Restore). This dialog lists them
  newest-first and lets the operator preview + roll back to any prior
  state. Rollback is itself reversible (a fresh pre_restore snapshot
  is stacked on top server-side).
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, History, Undo2, Eye, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

function _fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function TemplateVersionHistoryDialog({
  open, onOpenChange, templateCode, templateName, onRestored,
}) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);  // full version doc for preview pane
  const [previewLoading, setPreviewLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const loadVersions = useCallback(async () => {
    if (!templateCode) return;
    setLoading(true);
    try {
      const r = await api.get(
        `/exercise-program-templates/${templateCode}/versions`,
      );
      setVersions(r.data?.versions || []);
    } catch (e) {
      toast.error("Couldn't load history", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }, [templateCode]);

  useEffect(() => {
    if (open) { setPreview(null); loadVersions(); }
  }, [open, loadVersions]);

  async function openPreview(v) {
    setPreviewLoading(true);
    try {
      const r = await api.get(
        `/exercise-program-templates/${templateCode}/versions/${v.version_id}`,
      );
      setPreview(r.data?.version);
    } catch (e) {
      toast.error("Couldn't load version", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setPreviewLoading(false); }
  }

  async function restore(v) {
    if (!window.confirm(
      `Roll "${templateName}" back to this snapshot?\n\n`
        + `Reason: ${v.reason}\n`
        + `Captured: ${_fmt(v.created_at)}\n\n`
        + "The current live content will be snapshotted first, "
        + "so this rollback is itself reversible.",
    )) return;
    setRestoringId(v.version_id);
    try {
      await api.post(
        `/exercise-program-templates/${templateCode}`
          + `/versions/${v.version_id}/restore`,
      );
      toast.success(`Rolled "${templateName}" back to snapshot`);
      await loadVersions();
      setPreview(null);
      onRestored?.();
    } catch (e) {
      toast.error("Restore failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setRestoringId(null); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl w-[92vw] max-h-[86vh] overflow-hidden flex flex-col"
        data-testid="template-version-history-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4 text-indigo-700" />
            Version history — {templateName || templateCode}
          </DialogTitle>
          <DialogDescription>
            Every time this template is overwritten (via Replace-with or a
            previous Restore) its content is snapshotted here. Roll back
            any time — the current live state is snapshotted first, so a
            rollback is itself reversible.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-sm text-zinc-500 py-10 text-center">
              <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…
            </div>
          ) : preview ? (
            <VersionPreviewPane
              version={preview}
              loading={previewLoading}
              onBack={() => setPreview(null)}
              onRestore={() => restore(preview)}
              restoring={restoringId === preview.version_id}
            />
          ) : versions.length === 0 ? (
            <div className="text-sm text-zinc-500 py-10 text-center"
                 data-testid="template-version-history-empty">
              No history yet. Snapshots are captured on Replace-with or
              Restore.
            </div>
          ) : (
            <div className="space-y-2">
              {versions.map((v) => (
                <VersionRow
                  key={v.version_id}
                  v={v}
                  restoring={restoringId === v.version_id}
                  onPreview={() => openPreview(v)}
                  onRestore={() => restore(v)}
                />
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function VersionRow({ v, restoring, onPreview, onRestore }) {
  const isPreReplace = v.snapshot_type === "pre_replace";
  return (
    <div className="border border-zinc-200 rounded p-2 flex items-center gap-2"
         data-testid={`template-version-row-${v.version_id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate flex items-center gap-1">
          <Badge variant="outline"
                 className={cn("text-2xs",
                   isPreReplace ? "border-indigo-300 text-indigo-700"
                                : "border-amber-300 text-amber-700")}>
            {isPreReplace ? "pre-replace" : "pre-restore"}
          </Badge>
          <span className="truncate">{v.reason || "(no reason)"}</span>
        </div>
        <div className="text-2xs text-zinc-500">
          Snapshot of &quot;{v.name}&quot; · {v.section_count} sections
          · {v.exercise_count} exercises · {_fmt(v.created_at)}
          {v.created_by?.name ? ` · by ${v.created_by.name}` : ""}
        </div>
      </div>
      <Button size="sm" variant="ghost" onClick={onPreview}
              data-testid={`template-version-preview-${v.version_id}`}>
        <Eye className="h-3.5 w-3.5 mr-1" /> Preview
      </Button>
      <Button size="sm" variant="outline" onClick={onRestore}
              disabled={restoring}
              data-testid={`template-version-restore-${v.version_id}`}>
        {restoring
          ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          : <Undo2 className="h-3.5 w-3.5 mr-1" />}
        Restore
      </Button>
    </div>
  );
}


function VersionPreviewPane({ version, loading, onBack, onRestore, restoring }) {
  if (loading || !version) {
    return (
      <div className="text-sm text-zinc-500 py-10 text-center">
        <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading preview…
      </div>
    );
  }
  const content = version.content || {};
  const sections = content.sections || [];
  return (
    <div className="space-y-3" data-testid="template-version-preview-pane">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onBack}
                data-testid="template-version-preview-back">
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to history
        </Button>
        <div className="flex-1" />
        <Button size="sm" variant="default" onClick={onRestore}
                disabled={restoring}
                data-testid="template-version-preview-restore">
          {restoring
            ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
            : <Undo2 className="h-3.5 w-3.5 mr-1" />}
          Restore this version
        </Button>
      </div>
      <div className="border border-zinc-200 rounded p-3 space-y-2 bg-zinc-50/40">
        <div>
          <div className="text-2xs uppercase text-zinc-500">Name at snapshot</div>
          <div className="text-sm font-medium">{content.name || "(unnamed)"}</div>
        </div>
        {content.short_description && (
          <div>
            <div className="text-2xs uppercase text-zinc-500">Description</div>
            <div className="text-sm">{content.short_description}</div>
          </div>
        )}
        {(content.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {content.tags.map((t, i) => (
              <Badge key={i} variant="outline" className="text-2xs">{t}</Badge>
            ))}
          </div>
        )}
        <div>
          <div className="text-2xs uppercase text-zinc-500 mb-1">
            Sections ({sections.length})
          </div>
          <div className="space-y-1">
            {sections.map((s, si) => (
              <div key={si} className="border border-zinc-200 rounded bg-white p-2">
                <div className="text-xs font-medium">
                  {s.label || s.key || `Section ${si + 1}`}
                  <span className="text-zinc-500 font-normal ml-1">
                    · {(s.exercises || []).length} exercises
                  </span>
                </div>
                <ul className="mt-1 text-2xs text-zinc-700 list-disc pl-4">
                  {(s.exercises || []).slice(0, 8).map((ex, ei) => (
                    <li key={ei}>{ex.name || "(unnamed)"}</li>
                  ))}
                  {(s.exercises || []).length > 8 && (
                    <li className="text-zinc-500 italic">
                      …+{(s.exercises || []).length - 8} more
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
